import { formatDateForInput } from 'constants/Helpers';
import { getStoredId } from 'helpers/LocalStorage';

// Query param enums for GET operation/florist/orders and operation/florist/orders/overdue

export const OPS_LEVELS = {
  ALL: 0,
  MAIN: 1,
  SUB: 2,
  B2B: 3,
};

export const opsLevelOptions = [
  { value: OPS_LEVELS.MAIN, text: 'Main OPS' },
  { value: OPS_LEVELS.SUB, text: 'Sub-OPS' },
  { value: OPS_LEVELS.B2B, text: 'B2B OPS' },
  { value: OPS_LEVELS.ALL, text: 'All' },
];

// FloristOrderPrep - the Needs Prep / No-Prep filter.
export const FLORIST_ORDER_PREP = {
  ALL: 0,
  NEED_PREPARATION: 1,
  NO_PREPARATION: 2,
};

// FloristAssignedOrders - the status filter.
export const FLORIST_ASSIGNED_ORDERS = {
  ALL: 0,
  ASSIGNED: 1,
  NOT_ASSIGNED: 2,
  STARTED: 3,
  READY: 4,
};

export const statusFilterOptions = [
  { value: FLORIST_ASSIGNED_ORDERS.ALL, text: 'Status All' },
  { value: FLORIST_ASSIGNED_ORDERS.ASSIGNED, text: 'Assigned' },
  { value: FLORIST_ASSIGNED_ORDERS.NOT_ASSIGNED, text: 'Not assigned' },
  { value: FLORIST_ASSIGNED_ORDERS.STARTED, text: 'Started' },
  { value: FLORIST_ASSIGNED_ORDERS.READY, text: 'Ready' },
];

export const prepFilterOptions = [
  { value: FLORIST_ORDER_PREP.ALL, text: 'All' },
  { value: FLORIST_ORDER_PREP.NEED_PREPARATION, text: 'Needs Prep' },
  { value: FLORIST_ORDER_PREP.NO_PREPARATION, text: 'No-Prep' },
];

// GroupType
export const GROUP_TYPES = {
  FLORIST: 0,
};

// 0 = all florists on the orders endpoint
export const ALL_FLORISTS = 0;

// Views of the orders home page - the keys double as the `tab` query param.
export const ORDERS_TABS = {
  RANKED: 'ranked-orders',
  OVERDUE: 'overdue-orders',
  MY_TASKS: 'my-tasks',
  COMPLETED: 'completed-orders',
  DROPPED: 'dropped-orders',
  HOF: 'hof-dashboard',
};

// NotDoneState
export const NOT_DONE_STATES = {
  NONE: 1,
  AWAITING_REASSIGNMENT: 2,
  REASSIGNED: 3,
};

// AssignmentSource - how the florist got the order.
export const ASSIGNMENT_SOURCES = {
  1: 'Automatic',
  2: 'Manual',
  3: 'Self',
  4: 'HOF reassignment',
};

// ExperienceLevel
export const EXPERIENCE_LEVELS = {
  0: 'Junior',
  1: 'Intermediate',
  2: 'Senior',
};

// CancelOrderPreparationReason
export const DROP_REASONS = {
  0: 'Missing item in FC',
  1: 'Damaged item in FC',
  2: 'Poor quality',
  3: 'Wrong florist assignment',
  4: 'Quantity less than required',
  5: 'Out of stock',
};

export const defaultOrdersFilters = () => ({
  deliveryDate: formatDateForInput(new Date()),
  opsLevel: OPS_LEVELS.ALL,
  opsId: '',
  floristOrderPrep: FLORIST_ORDER_PREP.ALL,
  floristAssignedOrders: FLORIST_ASSIGNED_ORDERS.ALL,
  searchKey: '',
  tab: ORDERS_TABS.RANKED,
  page: 1,
});

// opsId is always sent: the picked OPS when there is one, otherwise the logged in
// user's main OPS. It has to stay inside the OPS scope of the opsId header, else the
// endpoints return 400.
export const getMainOpsId = () => getStoredId('opsId');

const resolveOpsId = (opsId) => opsId || getMainOpsId();

/**
 * Builds the query params for the orders / orders-overdue endpoints.
 */
export const buildOrdersParams = ({
  deliveryDate,
  opsLevel = OPS_LEVELS.ALL,
  opsId,
  page = 1,
  floristOrderPrep = FLORIST_ORDER_PREP.ALL,
  floristAssignedOrders = FLORIST_ASSIGNED_ORDERS.ALL,
  searchKey = '',
  floristId = ALL_FLORISTS,
  isDone = false,
  type = GROUP_TYPES.FLORIST,
}) => {
  const scopedOpsId = resolveOpsId(opsId);
  const params = {
    deliveryDate: formatDateForInput(deliveryDate),
    opsLevel: +opsLevel,
    page: +page || 1,
    floristId: +floristId,
    floristOrderPrep: +floristOrderPrep,
    floristAssignedOrders: +floristAssignedOrders,
    isDone,
    type,
  };
  if (scopedOpsId) {
    params.opsId = +scopedOpsId;
  }
  if (searchKey) {
    params.searchKey = searchKey;
  }
  return params;
};

/**
 * The florists list (skill, online status, current load) is scoped by OPS only and is
 * shown in full - no paging.
 */
export const buildFloristsParams = ({ opsLevel = OPS_LEVELS.ALL, opsId, search = '' }) => {
  const params = { opsLevel: +opsLevel };
  if (opsId) {
    params.opsIds = +opsId;
  }
  if (search) {
    params.search = search;
  }
  return params;
};

/**
 * The dropped orders list is scoped by OPS only - no date, search or prep filters.
 * One OPS can be picked, so opsIds goes out as a single value, not a repeated key.
 */
export const buildDroppedOrdersParams = ({
  opsLevel = OPS_LEVELS.ALL,
  opsId,
  page = 1,
  pageSize = 30,
}) => {
  const params = {
    opsLevel: +opsLevel,
    page: +page || 1,
    pageSize,
  };
  if (opsId) {
    params.opsIds = +opsId;
  }
  return params;
};

/**
 * orders/assigned takes a narrower set - florist, OPS, date and the two filters.
 */
export const buildAssignedOrdersParams = ({
  deliveryDate,
  opsId,
  floristId,
  page = 1,
  floristOrderPrep = FLORIST_ORDER_PREP.ALL,
  floristAssignedOrders = FLORIST_ASSIGNED_ORDERS.ALL,
}) => {
  const scopedOpsId = resolveOpsId(opsId);
  const params = {
    deliveryDate: formatDateForInput(deliveryDate),
    page: +page || 1,
    floristId: +floristId || ALL_FLORISTS,
    floristOrderPrep: +floristOrderPrep,
    floristAssignedOrders: +floristAssignedOrders,
  };
  if (scopedOpsId) {
    params.opsId = +scopedOpsId;
  }
  return params;
};

// `type` on the OperationCenter payload tells a Sub-OPS from a B2B OPS.
export const OPS_TYPES = {
  SUB: 1,
  B2B: 2,
};

const opsLevelFromType = (type) => (type === OPS_TYPES.B2B ? OPS_LEVELS.B2B : OPS_LEVELS.SUB);

/**
 * OperationCenter/sub-and-b2b-ops/{mainOpsId} feeds the OPS dropdown. It answers with
 * { id, name, type } rows, normalised here so the UI only deals with
 * { id, name, opsLevel }.
 */
export const normalizeOpsList = (data) => {
  const rows = Array.isArray(data) ? data : [data];
  return rows
    .filter((ops) => ops?.id != null)
    .map((ops) => ({
      id: ops.id,
      name: ops.name ?? `OPS ${ops.id}`,
      opsLevel: opsLevelFromType(ops.type),
    }));
};

/**
 * Sub-OPS and B2B OPS come from the same endpoint, so the list is filtered by the
 * selected level. Payloads that don't tag each OPS with a level fall back to the
 * full list rather than rendering an empty dropdown.
 */
export const filterOpsByLevel = (opsList, opsLevel) => {
  const list = Array.isArray(opsList) ? opsList : [];
  const filtered = list.filter((ops) => ops.opsLevel === opsLevel);
  return filtered.length > 0 ? filtered : list.filter((ops) => ops.opsLevel == null);
};
